import { SortingState } from "@tanstack/react-table";
import TableContainer from "Common/TableContainer";
import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";
import SearchBox from "Components/SearchBox/SearchBox";
import { app_modules } from "helpers/apps_modules_list";
import {
  Fragment,
  useEffect,
  useRef,
  useState
} from "react";
import { Button, Card, Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportHsnCodeCSV,
  fetchhsnCodes as OnfetchHsnCodes,
  updateStatusInhsnCode as onUpdateStatusInHsnCode
} from "slices/hsn-code/thunk";

const HSNCode = () => {
  document.title = "HSNCode List";
  const dispatch = useDispatch<any>();

  // Redux selector for hsn-code state
  const selectHsnCodes = createSelector(
    (state: any) => state.HSNCode,
    (hsnCode) => ({
      HsnCodes: hsnCode.hsnCodeList,
      rowCount: hsnCode.rowCount,
      loading: hsnCode.loading,
      error: hsnCode.error,
    })
  );

  const { HsnCodes, rowCount, loading, error } = useSelector(selectHsnCodes);

  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.permissions || [];
  const master_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.master?.app_name
  );
  const hsn_permissions = master_permissions?.modules_permissions?.find(
    (item: any) =>
      item?.modules_name === app_modules?.master?.modules?.hsn_code
  );

  //*// For redirecting back to this page and get the same paginated pageIndex
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page") || "0");

  const [pageIndex, setPageIndex] = useState(pageParam);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(rowCount);

  // State for edit functionality
  const [editHsnCode, setEditHsnCode] = useState<boolean>(false);
  const [columnFilters, setColumnFilters] = useState<any>();
  const [edit, setEdit] = useState<any>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const [filterValue, setFilterValue] = useState<any[]>([]);

  useEffect(() => {
    dispatch(
      OnfetchHsnCodes({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [dispatch, pageIndex, pageSize, sorting, search, sortField, sortDirection]);

  const prevFilters = useRef(filterValue);

  useEffect(() => {
    if (JSON.stringify(prevFilters.current) !== JSON.stringify(filterValue)) {
      prevFilters.current = filterValue;
      dispatch(
        OnfetchHsnCodes({
          page: 1, // Always use page 1 when filters change
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
        })
      );
    }
  }, [filterValue]);

  useEffect(() => { }, [sorting, pageSize, columnFilters]);

  useEffect(() => {
    setData(HsnCodes);
  }, [HsnCodes]);

  //*// All pagination Issues(resolved) when redirecting from the edit or view page
  // Reset pagenumber to 0 when not first render
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    handlePageChange(0);
  }, [sorting, pageSize, columnFilters, search]);
  const handlePageChange = (newPage: number) => {
    setPageIndex(newPage);
    setSearchParams({ page: newPage.toString() });
  };
  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);
  //*//

  // Handle edit action
  const handleEdit = (item: any) => {
    setEditHsnCode(!editHsnCode);
    setEdit({
      id: item.id,
      HsnCodeName: item.HsnCode_name,
      state: item.state_name,
      status: item.is_active,
      createdBy: item.created_by,
      updatedBy: item.updated_by,
      createDate: item.created_at,
      updatedDate: item.updated_at,
    });
  };
  const handleStatusToggle = async (pkg: any) => {
    try {
      const updatedStatus = !pkg.status;
      const updatedHsnCode = {
        id: pkg.hsn_code_id,
        status: updatedStatus,
      };

      await dispatch(onUpdateStatusInHsnCode(updatedHsnCode))
        .unwrap()
        .then(() => {
          toast.success(
            `HSN Code ${updatedStatus ? "activated" : "deactivated"
            } successfully`
          );
        });

      dispatch(
        OnfetchHsnCodes({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
        })
      );
    } catch (err: any) {
      toast.error("Failed to Update Status");
    }
  };

  const handleExport = async () => {
    try {
      // const { blob, filename } = await exportHsnCodeCSV();
      const { blob, filename } = await exportHsnCodeCSV({
        search,
        filters: filterValue,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const DynamicAction =
    hsn_permissions?.permissions?.Update === true
      ? [
        {
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => (
            <ul className="d-flex gap-2 list-unstyled mb-0">
              <li>
                <Link
                  to={`/edit-hsn-code/${row.original.hsn_code_id}`}
                  state={{ hsnData: row.original }}
                  className="btn btn-subtle-secondary btn-icon btn-sm edit-item-btn"
                  onClick={() => handleEdit(row.original)}
                >
                  <i className="ph-pencil"></i>
                </Link>
              </li>
            </ul>
          ),
        },
      ]
      : [];

  const columns =
    // useMemo(
    //   () =>
    [
      // {
      //   header: "Sr. No.",
      //   id: "sr_no", // important: since there's no accessorKey
      //   enableSorting: false,
      //   enableColumnFilter: false,
      //   cell: ({ row }: { row: any }) => {
      //     return row.index + 1 + pageIndex * pageSize;
      //   },
      // },
      {
        header: "HSN Code",
        accessorKey: "hsn_code",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/hsn/column-filter-dropdown/?column_filter=hsn_code",
      },
      {
        header: "HSN Code Description",
        accessorKey: "hsn_code_description",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              width: "250px",
            }}
          >
            {row.original.hsn_code_description || "-"}
          </div>
        ),
        // filterType: "dropdown" as const,
        // filterFetchUrl: "hsn/dropdown/hsn_code_description/",
      },
      {
        header: "GST%",
        accessorKey: "gst__gst_percentage",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/hsn/column-filter-dropdown/?column_filter=gst__gst_percentage",
        // cell: ({ getValue }: any) => `${getValue()}%`,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const pkg = row.original;

          return (
            <div
              style={{ fontSize: "1.5rem", cursor: "pointer" }}
              onClick={() => handleStatusToggle(pkg)}
            >
              <i
                className={`bi ${pkg.status
                    ? "bi-toggle-on text-primary"
                    : "bi-toggle-off text-danger"
                  }`}
              // style={{ color: "#0073BB" }}
              />
            </div>
          );
        },
      },
      {
       header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_name ?? "N/A";
          const date = formatDate(row.original.created_at);
          return `  ${name}  -  ${date}  `; // extra spaces for visual padding
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_name ?? "N/A";
          const date = formatDate(row.original.updated_at);
          return `  ${name}  -  ${date}  `; // spaces before/after the name
        },
      },
      ...DynamicAction,
    ];
  //   [dispatch, HsnCodes]
  // );

  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">HSN Code</h6>
                    </Col>

                    {/* Search Input Box (reusable)*/}
                    <Col xl={2} md={3}>
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    <Col className="col-md-auto">
                      <div className="hstack gap-2">
                        <Button
                          className="btn btn-subtle-danger d-none"
                          id="remove-actions"
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </Button>
                        {/* {
                          hsn_permissions?.permissions?.View === true && */}
                        {/* <div
                            className="hstack gap-2 btn btn-light"
                            onClick={handleExport}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="bi bi-box-arrow-up align-baseline me-1"></i>{" "}
                            Export
                          </div> */}


                        {
                          hsn_permissions?.permissions?.Create === true &&
                          <Link
                            to="/add-hsn-code"
                            className="btn"
                            style={{
                              backgroundColor: "#0073BB",
                              borderColor: "#0073BB",
                              color: "#fff",
                            }}
                          >
                            <i className="bi bi-plus-circle align-baseline me-1"></i>{" "}
                            Add HSN Code
                          </Link>
                        }
                      </div>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  <LoaderErrorWrapper loading={loading} error={error}>
                    <TableContainer
                      columns={columns}
                      data={data}
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      // onPageChange={setPageIndex}
                      onPageChange={handlePageChange} // for persisted pagination across pages
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      SearchPlaceholder="Search HsnCodes..."
                      // columnFilters={[]}
                      // setColumnFilters={function (filters: ColumnFiltersState): void {
                      //   throw new Error("Function not implemented.");
                      // } }
                      manualSorting={true}
                      colValue={setFilterValue}
                    />
                  </LoaderErrorWrapper>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
};

export default HSNCode;
